import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { CreatePaymentRequest, DebtDirection } from '../../../types/debt';

interface RecordPaymentDialogProps {
  open: boolean;
  contactId: number;
  direction: DebtDirection;
  onClose: () => void;
  onSubmit: (data: CreatePaymentRequest) => Promise<void>;
}

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

export default function RecordPaymentDialog({
  open,
  contactId,
  direction,
  onClose,
  onSubmit,
}: RecordPaymentDialogProps) {
  const [amount, setAmount] = useState('');
  const [entryDate, setEntryDate] = useState(todayIsoDate());
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setAmount('');
    setEntryDate(todayIsoDate());
    setNote('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Сума оплати має бути додатною');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        contactId,
        direction,
        amount: parsedAmount,
        entryDate: `${entryDate}T00:00:00`,
        note: note.trim() || undefined,
      });
      resetForm();
      onClose();
    } catch (err) {
      console.error('Failed to record payment:', err);
      setError('Не вдалося зберегти оплату. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Записати оплату</DialogTitle>
        <DialogContent>
          <TextField
            type="number"
            label="Сума (USD)"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
          <TextField
            type="date"
            label="Дата оплати"
            fullWidth
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            disabled={loading}
            required
            margin="normal"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Нотатка"
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={loading}
            margin="normal"
            helperText="Необов'язково"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
